import { useEffect, useId, useRef, useState } from "react";
import confetti from "canvas-confetti";
import { Loader2 } from "lucide-react";
import AnswerRecap from "@/components/storyboard/AnswerRecap";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import { GradedFeedback } from "@/components/storyboard/GradedFeedback";
import InputPrivacyNotice, {
  NO_PERSONAL_DATA_HINT,
} from "@/components/storyboard/InputPrivacyNotice";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import { Textarea } from "@/components/ui/textarea";
import { QuizActionButton } from "@/components/storyboard/quiz/QuizActionButton";
import { QuizAttemptHint } from "@/components/storyboard/quiz/QuizAttemptHint";
import { buildTextItem } from "@/components/storyboard/quiz/review/buildReviewItem";
import {
  canRetry,
  NO_ATTEMPTS_LEFT_FEEDBACK,
  retryLabel,
  ValidationAttemptsExceededError,
} from "@/lib/validationSubmission";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { QuizFreeTextEntryData } from "@/types/storyboard";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import type { QuizReview } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizFreeTextState {
  userAnswer: string;
  isSubmitted: boolean;
  isCorrect: boolean | null;
  /** Normalized four-level grade key from the LLM validation, when available. */
  grade?: string;
  /** The verdict text, kept so a resumed quiz still shows what was said. */
  feedback?: string;
  /** Server-counted submissions; absent until the first verdict comes back. */
  attemptCount?: number;
  /** Attempt cap of this quiz type, null when it allows unlimited retries. */
  maxAttempts?: number | null;
}

export interface QuizFreeTextProps extends HiddenVerdictProps {
  data: QuizFreeTextEntryData;
  onValidate: (answer: string) => Promise<ValidationResult>;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizFreeTextState;
  onPartialChange?: (partial: QuizFreeTextState, index: number) => void;
}

const QuizFreeText = ({
  data,
  onValidate,
  onComplete,
  initialPartial,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizFreeTextProps) => {
  const [state, setState] = useState<QuizFreeTextState>(() => {
    if (initialPartial) return initialPartial;
    return { userAnswer: "", isSubmitted: false, isCorrect: null };
  });
  const [feedback, setFeedback] = useState(initialPartial?.feedback ?? "");
  const [isValidating, setIsValidating] = useState(false);
  const privacyNoticeId = useId();

  const hasInteractedRef = useRef<boolean>(initialPartial != null);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, 0);
  }, [state, onPartialChange]);

  const submitLabel = collectsWithoutVerdict(revealFeedback)
    ? (advanceLabel ?? "Nächste Frage")
    : "Überprüfen";
  const { instruction } = data;
  const { userAnswer, isSubmitted, isCorrect } = state;

  // A wrong answer with attempts left keeps the quiz open instead of scoring
  // it. Types without a cap report no budget at all and stay single-shot.
  const retryable = isSubmitted && canRetry({ ...state, correct: isCorrect });

  const handleChange = (value: string) => {
    hasInteractedRef.current = true;
    setState((prev) => ({ ...prev, userAnswer: value }));
  };

  const finish = (
    submittedAnswer: string,
    result: Pick<ValidationResult, "correct" | "feedback">
  ) => {
    const review: QuizReview = {
      items: [
        buildTextItem({
          prompt: instruction,
          userAnswer: submittedAnswer,
          isCorrect: result.correct === true,
          feedback: result.feedback,
        }),
      ],
    };
    onComplete?.(
      { correct: result.correct === true ? 1 : 0, total: 1 },
      review
    );
  };

  const handleSubmit = async () => {
    const submittedAnswer = state.userAnswer;
    if (collectsWithoutVerdict(revealFeedback)) {
      if (!submittedAnswer.trim()) return;
      await advanceAfterCollect(() => onValidate(submittedAnswer), onAdvance);
      return;
    }
    setIsValidating(true);
    try {
      const result = await onValidate(submittedAnswer);
      hasInteractedRef.current = true;
      setState((prev) => ({
        ...prev,
        isCorrect: result.correct,
        grade: result.grade,
        feedback: result.feedback,
        attemptCount: result.attemptCount,
        maxAttempts: result.maxAttempts,
        isSubmitted: true,
      }));
      setFeedback(result.feedback);
      // Scoring the answer ends the quiz, so it waits until no retry is left.
      if (!canRetry({ ...result, correct: result.correct })) {
        finish(submittedAnswer, result);
      }
      if (result.correct) {
        confetti({ particleCount: 150, spread: 80, origin: { y: 0.6 } });
      }
    } catch (error) {
      // The server is the authority on the budget: if it says the attempts are
      // gone, the answer stands as it is however many tries the UI believed it
      // had left.
      if (!(error instanceof ValidationAttemptsExceededError)) throw error;
      hasInteractedRef.current = true;
      setState((prev) => ({
        ...prev,
        isCorrect: prev.isCorrect ?? false,
        attemptCount: error.attemptCount,
        maxAttempts: error.maxAttempts,
        isSubmitted: true,
      }));
      setFeedback(NO_ATTEMPTS_LEFT_FEEDBACK);
      finish(submittedAnswer, {
        correct: state.isCorrect ?? false,
        feedback: NO_ATTEMPTS_LEFT_FEEDBACK,
      });
    } finally {
      setIsValidating(false);
    }
  };

  const handleRetry = () => {
    // The answer stays put so the learner can build on it rather than retype.
    setState((prev) => ({
      ...prev,
      isSubmitted: false,
      isCorrect: null,
      grade: undefined,
    }));
    setFeedback("");
  };

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <div className="prose quiz-question max-w-full font-semibold text-primary">
          <CourseMarkdown>{instruction}</CourseMarkdown>
        </div>
      </div>

      <div className="flex flex-1 flex-col items-start justify-start gap-4 pt-8">
        {isSubmitted && isCorrect !== null ? (
          <>
            <AnswerRecap value={userAnswer} className="max-w-2xl" />

            {/* Above the verdict: the learner should know another go is
                coming before reading what went wrong. */}
            {isCorrect !== true && (
              <QuizAttemptHint
                attemptCount={state.attemptCount}
                maxAttempts={state.maxAttempts}
                className="max-w-2xl"
              />
            )}

            {feedback && (
              <GradedFeedback
                result={{ correct: isCorrect, grade: state.grade, feedback }}
                className="w-full max-w-2xl"
              />
            )}
          </>
        ) : (
          <div className="flex w-full max-w-2xl flex-col gap-4">
            <TaskInstructionHint>Formuliere deine Antwort</TaskInstructionHint>
            {/* sr-only: same privacy copy as the placeholder, linked via
                aria-describedby so screen readers keep the warning after typing. */}
            <InputPrivacyNotice id={privacyNoticeId} className="sr-only" />
            <Textarea
              rows={10}
              value={userAnswer}
              placeholder={NO_PERSONAL_DATA_HINT}
              onChange={(e) => handleChange(e.target.value)}
              disabled={isValidating}
              aria-describedby={privacyNoticeId}
              className="w-full rounded-lg border-2 border-primary bg-background shadow-sm resize-none transition-colors field-sizing-fixed focus-visible:border-primary focus-visible:ring-primary/40"
            />
          </div>
        )}
      </div>

      <div className="shrink-0 pt-4">
        {/* eslint-disable-next-line no-nested-ternary */}
        {!isSubmitted ? (
          <div className="flex justify-center">
            <QuizActionButton
              variant="default"
              onClick={handleSubmit}
              disabled={!userAnswer.trim() || isValidating}
              withArrow={!isValidating}
              className={
                isValidating
                  ? "opacity-80 animate-pulse cursor-wait"
                  : undefined
              }
            >
              {isValidating ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="animate-spin w-5 h-5 shrink-0" />
                  <span>Wird ausgewertet…</span>
                </span>
              ) : (
                submitLabel
              )}
            </QuizActionButton>
          </div>
        ) : retryable ? (
          <div className="flex justify-center">
            <QuizActionButton variant="default" onClick={handleRetry}>
              {retryLabel()}
            </QuizActionButton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default QuizFreeText;
